import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { PixiSettings } from "../../../../pixiSettings";

// Native skeleton dimensions (from bonus_buy.json)
const NATIVE_WIDTH = 300;
const NATIVE_HEIGHT = 300;

// Text configuration — matches WinAnimationController button text style
const TEXT_CONFIG = {
  FONT_SIZE: 34,
  FONT_WEIGHT: "800",
  SLOT_NAME: "txt_slot_buy_bonus",
};

/**
 * BuyBonusController — spine-based buy bonus button.
 * Animations: idle (looping), hover (once → idle), click (once → idle).
 */
class BuyBonusController {
  constructor(container, width, height, buttonText, deviceConfig, callbacks) {
    this.container = container;
    this.targetWidth = width;
    this.targetHeight = height;
    this.buttonText = buttonText || "BUY BONUS";
    this.deviceConfig = deviceConfig || { scale: 1, xOffset: 0, yOffset: 0 };
    this.callbacks = callbacks || {};
    this.spine = null;
    this.isInitialized = false;
    this.currentAnimation = null;
    this.textContainer = null;

    this.topText = null;

    requestAnimationFrame(() => {
      this.initialize();
    });
  }

  initialize() {
    this.spine = this.createSpine();
    if (!this.spine) return;

    this.container.addChild(this.spine);
    this.spine.position.set(0, 0);
    this.spine.zIndex = 2;
    this.spine.visible = true;
    this.spine.skeleton.setToSetupPose();

    // Make spine interactive — uses natural spine bounds as hit area
    this.spine.eventMode = "static";
    this.spine.cursor = "pointer";

    // Bind pointer events
    this.spine.on("pointertap", () => {
      if (this.callbacks.onTap) this.callbacks.onTap();
    });
    this.spine.on("pointerover", () => {
      if (this.callbacks.onHover) this.callbacks.onHover();
    });
    this.spine.on("pointerout", () => {
      if (this.callbacks.onOut) this.callbacks.onOut();
    });

    this.applyScaling();
    this.initializeText();
    this.playIdle();
    this.isInitialized = true;
  }

  createSpine() {
    try {
      const skeletonAlias = "bonus_buy-skeleton";
      const atlasAlias = "bonus_buy-atlas";

      if (
        !PIXI.Assets.cache.has(skeletonAlias) ||
        !PIXI.Assets.cache.has(atlasAlias)
      ) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine?.skeleton || !spine?.state) return null;
      if (!spine.skeleton.data?.animations) return null;

      return spine;
    } catch {
      return null;
    }
  }

  applyScaling() {
    if (!this.spine || !this.targetWidth || !this.targetHeight) return;
    if (!this.spine.scale || !this.spine.parent) return;

    const nativeW = this.spine.skeleton.data.width || NATIVE_WIDTH;
    const nativeH = this.spine.skeleton.data.height || NATIVE_HEIGHT;
    const cfg = this.deviceConfig;

    const scaleX = this.targetWidth / nativeW;
    const scaleY = this.targetHeight / nativeH;
    const scale = Math.min(scaleX, scaleY) * (cfg.scale || 1);

    try {
      this.spine.scale.set(scale);
      this.spine.position.set(cfg.xOffset || 0, cfg.yOffset || 0);
    } catch {
      // Silent fail
    }
  }

  initializeText() {
    if (!this.spine?.skeleton) return;

    const slot = this.spine.skeleton.findSlot(TEXT_CONFIG.SLOT_NAME);
    if (!slot) return;

    this.textContainer = new PIXI.Container();
    this.textContainer.name = "buy_bonus_text_slot";

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const fontSize = TEXT_CONFIG.FONT_SIZE;

    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      fontSize,
      fontWeight: TEXT_CONFIG.FONT_WEIGHT,
      align: "center",
      letterSpacing: 1,
      padding: 10,
      wordWrap: true,
      wordWrapWidth: 1,
      lineHeight: fontSize * 1.05,
      leading: -14,
    };

    // Layer 1: Shadow text (bottom)
    const shadowStyle = new PIXI.TextStyle({
      ...sharedProps,
      ...primaryStyle.shadow,
    });

    this.shadowText = new PIXI.Text(this.buttonText, shadowStyle);
    this.shadowText.anchor.set(0.5);
    this.shadowText.x = 0;
    this.shadowText.y = primaryStyle.shadowOffsetY || 3;

    // Layer 2: Top text (white, on top)
    const topStyle = new PIXI.TextStyle({
      ...sharedProps,
      ...primaryStyle.top,
    });

    this.topText = new PIXI.Text(this.buttonText, topStyle);
    this.topText.anchor.set(0.5);
    this.topText.x = 0;
    this.topText.y = 0;

    this.textContainer.addChild(this.shadowText);
    this.textContainer.addChild(this.topText);

    this.spine.addSlotObject(TEXT_CONFIG.SLOT_NAME, this.textContainer);
  }

  /**
   * Smooth transition helper — avoids visual flashes by cross-fading
   * Uses addAnimation with 0.1s mix when an animation is already playing
   */
  smoothTransition(animName, loop) {
    if (!this.spine?.state) return;

    const tracks = this.spine.state.tracks;
    const hasCurrentAnim = tracks && tracks.length > 0 && tracks[0];

    if (hasCurrentAnim) {
      const currentName = tracks[0].animation?.name;
      if (currentName === animName) return; // Already playing
      this.spine.state.addAnimation(0, animName, loop, 0.1);
    } else {
      this.spine.state.setAnimation(0, animName, loop);
    }
  }

  playIdle() {
    if (!this.spine?.state) return;
    this.currentAnimation = "idle";
    this.spine.state.clearListeners();
    this.smoothTransition("idle", true);
  }

  playHover() {
    if (!this.spine?.state) return;
    if (this.currentAnimation === "hover") return;

    const hasHover = this.spine.skeleton.data.animations.find(
      (a) => a.name === "hover",
    );
    if (!hasHover) return;

    this.currentAnimation = "hover";
    this.spine.state.clearListeners();
    this.smoothTransition("hover", true); // Loop while pointer is inside
  }

  playClick(onComplete) {
    if (!this.spine?.state) {
      if (onComplete) onComplete();
      return;
    }

    const clickAnim = this.spine.skeleton.data.animations.find(
      (a) => a.name === "click",
    );
    if (!clickAnim) {
      if (onComplete) onComplete();
      return;
    }

    this.currentAnimation = "click";
    this._clickCallback = onComplete || null;
    this.spine.state.clearListeners();
    this.spine.state.setAnimation(0, "click", false);

    // Fire callback at 50% of the click animation duration
    // so the popup opens while the animation is still playing
    if (this._clickCallback) {
      const halfDuration = (clickAnim.duration * 1000) / 2;
      this._clickTimer = setTimeout(() => {
        const cb = this._clickCallback;
        this._clickCallback = null;
        if (cb) cb();
      }, halfDuration);
    }

    // Queue idle/hover after click finishes
    this.spine.state.addListener({
      complete: (entry) => {
        if (entry.animation?.name === "click") {
          this.spine.state.clearListeners();
          this.currentAnimation = "hover";
          this.smoothTransition("hover", true);
        }
      },
    });
  }

  /**
   * Play click animation on loop (used while popup is active)
   */
  playClickLoop() {
    if (!this.spine?.state) return;

    const hasClick = this.spine.skeleton.data.animations.find(
      (a) => a.name === "click",
    );
    if (!hasClick) return;

    this.currentAnimation = "click_loop";
    this.spine.state.clearListeners();
    this.spine.state.setAnimation(0, "click", true);
  }

  /**
   * Stop click loop and return to idle
   */
  stopClickLoop() {
    if (!this.spine?.state) return;
    if (this.currentAnimation !== "click_loop") return;
    this.playIdle();
  }

  returnToIdle() {
    // Don't interrupt a click that has a pending callback (e.g. opening popup)
    if (this.currentAnimation === "click" && this._clickCallback) return;
    // Don't interrupt click loop
    if (this.currentAnimation === "click_loop") return;

    if (this.currentAnimation !== "click") {
      this.playIdle();
    } else {
      // Click without callback — queue idle after it finishes
      this.spine.state.clearListeners();
      this.spine.state.addListener({
        complete: (entry) => {
          if (entry.animation?.name === "click") {
            this.playIdle();
          }
        },
      });
    }
  }

  updateDimensions(width, height, deviceConfig) {
    const changed = this.targetWidth !== width || this.targetHeight !== height;
    this.targetWidth = width;
    this.targetHeight = height;
    if (deviceConfig) {
      this.deviceConfig = deviceConfig;
    }

    if (this.spine && this.isInitialized && changed) {
      this.applyScaling();
      // Rebuild text with new size ratio so font matches spine scale
      // But preserve the current animation — don't reset spine state
      this.cleanupText();
      this.initializeText();
    }
  }

  cleanupText() {
    if (!this.textContainer) return;
    try {
      if (this.spine) {
        this.spine.removeSlotObject(TEXT_CONFIG.SLOT_NAME, this.textContainer);
      }

      if (this.shadowText) this.shadowText.destroy();
      if (this.topText) this.topText.destroy();
      this.textContainer.destroy();
    } catch {
      // Silent fail
    }
    this.textContainer = null;
    this.shadowText = null;
    this.topText = null;
  }

  setDisabled(disabled) {
    if (this.spine) {
      this.spine.alpha = disabled ? 0.7 : 1;
      this.spine.eventMode = disabled ? "none" : "static";
      this.spine.cursor = disabled ? "default" : "pointer";
    }
  }

  cleanup() {
    this.cleanupText();

    if (this._clickTimer) {
      clearTimeout(this._clickTimer);
      this._clickTimer = null;
    }

    if (!this.spine) return;
    try {
      if (this.spine.state) {
        this.spine.state.clearListeners();
        this.spine.state.clearTracks();
      }
      if (this.container?.children?.includes(this.spine)) {
        this.container.removeChild(this.spine);
      }
      this.spine.destroy();
    } catch {
      // Silent fail
    } finally {
      this.spine = null;
      this.isInitialized = false;
    }
  }
}

export default BuyBonusController;
